/**
 * Revenue Tracking Service for HoneyDo Platform
 * 
 * Tracks all revenue-critical events and user journey stages
 * that correlate with subscription conversions and customer lifetime value.
 * 
 * Features:
 * - Subscription funnel tracking
 * - Feature usage correlation with conversions
 * - Customer lifecycle events
 * - A/B testing support
 * - Cohort analysis data collection
 */

import { analytics, trackConversion, trackEvent } from './analytics';
import { errorTracking } from './errorTracking';
import { appConfig } from '../config/app.config';

// Revenue event types (subscription-primary, contractor referrals secondary)
export type RevenueEventType = 
  | 'subscription_started'
  | 'subscription_upgraded'
  | 'subscription_downgraded'
  | 'subscription_canceled'
  | 'subscription_renewed'
  | 'free_trial_started'
  | 'free_trial_converted'
  | 'contractor_referral_viewed'
  | 'contractor_referral_clicked'
  | 'contractor_referral_contacted'
  | 'contractor_referral_booked'
  | 'contractor_referral_completed'
  | 'onboarding_completed'
  | 'high_value_action'
  | 'task_created'
  | 'family_invite_sent';

// User types (adult-only platform)
export type PlatformUserType = 'parent' | 'spouse' | 'roommate' | 'senior';

// Revenue event data
export interface RevenueEvent {
  event_type: RevenueEventType;
  user_id: string;
  family_id?: string;
  user_type?: PlatformUserType;
  // Subscription-related fields
  subscription_tier?: string;
  subscription_value?: number;
  // Contractor referral fields
  referral_value?: number;
  contractor_category?: string;
  referral_source?: string;
  metadata?: Record<string, any>;
}

// User journey stages for conversion funnel (subscription-focused)
export type UserJourneyStage = 
  | 'visitor'
  | 'registered'
  | 'trial_user'
  | 'paid_subscriber'
  | 'premium_subscriber'
  | 'contractor_interested'
  | 'referral_user'
  | 'power_user'
  | 'inactive';

// Feature usage events that correlate with upgrades
export interface FeatureUsageEvent {
  feature_name: string;
  user_id: string;
  usage_count: number;
  is_premium_feature?: boolean;
  limit_reached?: boolean;
  contractor_referral_related?: boolean;
  referral_generated?: boolean;
}

class RevenueTrackingService {
  private userJourneyStage: Map<string, UserJourneyStage> = new Map();
  private featureUsageCounts: Map<string, Map<string, number>> = new Map(); // userId -> feature -> count
  
  // Subscription revenue (PRIMARY REVENUE MODEL)
  private readonly SUBSCRIPTION_REVENUE = {
    basic: { monthly: 4.99, annual: 59.88 },
    family: { monthly: 9.99, annual: 119.88 },
    premium: { monthly: 19.99, annual: 239.88 }
  };

  // Contractor referral revenue estimates (SECONDARY REVENUE)
  private readonly CONTRACTOR_REVENUE = {
    plumbing: 150,
    electrical: 120,
    carpentry: 100,
    painting: 80,
    landscaping: 90,
    general: 75
  };

  // Platform limits by subscription tier
  private readonly PLATFORM_LIMITS = {
    free: {
      tasks_per_month: 10,
      family_members: 2,
      contractor_referrals: 5,
      storage_gb: 1
    },
    basic: {
      tasks_per_month: -1,
      family_members: 4,
      contractor_referrals: -1,
      storage_gb: 5
    },
    family: {
      tasks_per_month: -1,
      family_members: 8,
      contractor_referrals: -1,
      storage_gb: 25
    },
    premium: {
      tasks_per_month: -1,
      family_members: -1,
      contractor_referrals: -1,
      storage_gb: 100
    }
  };

  /**
   * Track a revenue-critical event
   */
  trackRevenueEvent(event: RevenueEvent) {
    const eventData = {
      ...event,
      timestamp: Date.now(),
      environment: appConfig.environment,
      referral_estimate: event.contractor_category ? this.CONTRACTOR_REVENUE[event.contractor_category as keyof typeof this.CONTRACTOR_REVENUE] : undefined
    };

    // Track in analytics
    if (this.isRevenueEvent(event.event_type)) {
      trackConversion({
        event_name: this.mapToRevenueEvent(event.event_type),
        user_id: event.user_id,
        value: event.referral_value || (event.contractor_category ? this.CONTRACTOR_REVENUE[event.contractor_category as keyof typeof this.CONTRACTOR_REVENUE] : 0),
        currency: 'USD',
        user_type: event.user_type,
        family_id: event.family_id
      });
    }

    // Track general event
    trackEvent({
      event_name: event.event_type,
      event_category: 'conversion',
      user_id: event.user_id,
      value: event.subscription_value || event.referral_value || 0,
      custom_parameters: eventData
    });

    // Update user journey stage
    this.updateUserJourneyStage(event.user_id, event.event_type);

    // Special handling for specific events
    switch (event.event_type) {
      case 'contractor_referral_completed':
        this.handleReferralCompleted(event);
        break;
      case 'contractor_referral_clicked':
        this.handleReferralClicked(event);
        break;
      case 'onboarding_completed':
        this.handleOnboardingCompleted(event);
        break;
    }

    console.log(`[Revenue] ${event.event_type}:`, eventData);
  }

  private isRevenueEvent(eventType: RevenueEventType): boolean {
    return [
      // Primary revenue: Subscriptions
      'subscription_started',
      'subscription_upgraded',
      'subscription_renewed',
      'free_trial_converted',
      // Secondary revenue: Contractor referrals
      'contractor_referral_booked',
      'contractor_referral_completed',
      'contractor_referral_clicked'
    ].includes(eventType);
  }

  private mapToRevenueEvent(eventType: RevenueEventType): 'sign_up' | 'first_task' | 'subscription_start' | 'subscription_upgrade' | 'trial_start' | 'trial_convert' {
    switch (eventType) {
      // Subscription events (primary revenue)
      case 'subscription_started':
        return 'subscription_start';
      case 'subscription_upgraded':
        return 'subscription_upgrade';
      case 'subscription_renewed':
        return 'subscription_start'; // Map renewal to start for simplicity
      case 'free_trial_converted':
        return 'trial_convert';
      // Contractor events mapped to closest conversion equivalent
      case 'contractor_referral_clicked':
        return 'first_task'; // Map contractor interaction to first task completion
      case 'contractor_referral_booked':
        return 'subscription_start'; // Map booking to subscription equivalent
      case 'contractor_referral_completed':
        return 'subscription_upgrade'; // Map completion to upgrade equivalent
      default:
        return 'first_task'; // Default to first_task for unmapped events
    }
  }

  private updateUserJourneyStage(userId: string, eventType: RevenueEventType) {
    const currentStage = this.userJourneyStage.get(userId) || 'visitor';
    let newStage: UserJourneyStage = currentStage;

    switch (eventType) {
      // Primary revenue events (subscription)
      case 'free_trial_started':
        if (currentStage === 'visitor' || currentStage === 'registered') {
          newStage = 'trial_user';
        }
        break;
      case 'subscription_started':
      case 'free_trial_converted':
        newStage = 'paid_subscriber';
        break;
      case 'subscription_upgraded':
        if (['trial_user', 'paid_subscriber'].includes(currentStage)) {
          newStage = 'premium_subscriber';
        }
        break;
      // Secondary revenue events (contractor referrals)
      case 'contractor_referral_completed':
        if (!['paid_subscriber', 'premium_subscriber'].includes(currentStage)) {
          newStage = 'referral_user';
        }
        break;
      case 'contractor_referral_clicked':
      case 'contractor_referral_contacted':
        if (currentStage === 'visitor' || currentStage === 'registered') {
          newStage = 'contractor_interested';
        }
        break;
      case 'onboarding_completed':
        if (currentStage === 'visitor') newStage = 'registered';
        break;
      case 'high_value_action':
        if (['trial_user', 'paid_subscriber', 'contractor_interested', 'referral_user'].includes(currentStage)) {
          newStage = 'power_user';
        }
        break;
    }

    if (newStage !== currentStage) {
      this.userJourneyStage.set(userId, newStage);
      
      trackEvent({
        event_name: 'user_journey_stage_change',
        event_category: 'user_action',
        user_id: userId,
        custom_parameters: {
          from_stage: currentStage,
          to_stage: newStage,
          event_trigger: eventType
        }
      });
    }
  }

  private handleReferralCompleted(event: RevenueEvent) {
    trackEvent({
      event_name: 'referral_revenue_generated',
      event_category: 'conversion',
      user_id: event.user_id,
      custom_parameters: {
        contractor_category: event.contractor_category,
        referral_value: event.referral_value,
        referral_source: event.referral_source
      }
    });
  }

  private handleReferralClicked(event: RevenueEvent) {
    trackEvent({
      event_name: 'referral_lead_generated',
      event_category: 'conversion',
      user_id: event.user_id,
      custom_parameters: {
        contractor_category: event.contractor_category,
        referral_source: event.referral_source,
        lead_quality: event.metadata?.lead_quality
      }
    });
  }

  private handleOnboardingCompleted(event: RevenueEvent) {
    trackEvent({
      event_name: 'onboarding_completed',
      event_category: 'user_action',
      user_id: event.user_id,
      custom_parameters: {
        user_type: event.user_type,
        features_explored: event.metadata?.features_explored,
        referral_interest: event.metadata?.referral_interest
      }
    });
  }


  /**
   * Track feature usage that correlates with upgrades
   */
  trackFeatureUsage(usage: FeatureUsageEvent) {
    if (!this.featureUsageCounts.has(usage.user_id)) {
      this.featureUsageCounts.set(usage.user_id, new Map());
    }

    const userFeatures = this.featureUsageCounts.get(usage.user_id)!;
    const currentCount = userFeatures.get(usage.feature_name) || 0;
    const newCount = currentCount + usage.usage_count;
    userFeatures.set(usage.feature_name, newCount);

    // Track feature engagement
    trackEvent({
      event_name: 'feature_usage',
      event_category: 'user_action',
      user_id: usage.user_id,
      value: usage.usage_count,
      custom_parameters: {
        feature_name: usage.feature_name,
        total_usage: newCount,
        is_premium_feature: usage.is_premium_feature,
        limit_reached: usage.limit_reached
      }
    });

    // Check if feature usage indicates contractor referral opportunity
    if (usage.contractor_referral_related) {
      this.checkContractorOpportunity(usage.user_id, usage.feature_name, newCount);
    }

    // Track power user behavior
    if (newCount >= 10) {
      this.trackPowerUserBehavior(usage.user_id, usage.feature_name, newCount);
    }
  }

  private checkContractorOpportunity(userId: string, featureName: string, currentUsage: number) {
    // Track contractor referral opportunities based on task types
    const contractorRelevantTasks = [
      'plumbing', 'electrical', 'carpentry', 'painting', 'landscaping'
    ];

    if (contractorRelevantTasks.some(category => featureName.includes(category))) {
      this.trackRevenueEvent({
        event_type: 'contractor_referral_viewed',
        user_id: userId,
        contractor_category: featureName,
        metadata: {
          usage_count: currentUsage,
          referral_opportunity: true
        }
      });
    }
  }

  private trackPowerUserBehavior(userId: string, featureName: string, usageCount: number) {
    this.updateUserJourneyStage(userId, 'high_value_action');

    trackEvent({
      event_name: 'power_user_behavior',
      event_category: 'user_action',
      user_id: userId,
      custom_parameters: {
        feature_name: featureName,
        usage_count: usageCount,
        user_segment: 'power_user'
      }
    });
  }

  /**
   * Track A/B testing for conversion optimization
   */
  trackABTest(userId: string, testName: string, variant: string, converted: boolean = false) {
    trackEvent({
      event_name: 'ab_test_participation',
      event_category: 'system',
      user_id: userId,
      custom_parameters: {
        test_name: testName,
        variant: variant,
        converted: converted
      }
    });
  }

  /**
   * Track onboarding completion
   */
  trackOnboardingStep(userId: string, step: string, completed: boolean = true) {
    trackEvent({
      event_name: 'onboarding_step',
      event_category: 'user_action',
      user_id: userId,
      custom_parameters: {
        step: step,
        completed: completed
      }
    });

    if (step === 'complete' && completed) {
      this.trackRevenueEvent({
        event_type: 'onboarding_completed',
        user_id: userId
      });
    }
  }

  /**
   * Calculate subscription conversion probability based on user behavior
   */
  getConversionProbability(userId: string): number {
    const journeyStage = this.userJourneyStage.get(userId) || 'visitor';
    const featureUsage = this.featureUsageCounts.get(userId) || new Map();
    
    let score = 0;

    // Base score by journey stage (subscription conversion probability)
    switch (journeyStage) {
      case 'visitor': score = 0.05; break;
      case 'registered': score = 0.15; break;
      case 'trial_user': score = 0.25; break;
      case 'paid_subscriber': score = 0.90; break;
      case 'premium_subscriber': score = 0.95; break;
      case 'contractor_interested': score = 0.35; break;
      case 'referral_user': score = 0.60; break;
      case 'power_user': score = 0.85; break;
      case 'inactive': score = 0.02; break;
    }

    // Boost score based on feature usage
    let totalFeatureUsage = 0;
    let premiumFeatureUsage = 0;
    
    featureUsage.forEach((count, featureName) => {
      totalFeatureUsage += count;
      
      // Count premium feature usage
      if (this.isPremiumFeature(featureName)) {
        premiumFeatureUsage += count;
      }
    });

    // Higher engagement = higher conversion probability
    if (totalFeatureUsage > 10) score += 0.1;
    if (totalFeatureUsage > 25) score += 0.15;
    if (totalFeatureUsage > 50) score += 0.2;
    
    // Premium feature usage indicates higher conversion intent
    if (premiumFeatureUsage > 0) score += 0.2;
    if (premiumFeatureUsage > 5) score += 0.3;
    
    // Feature diversity indicates engagement
    if (featureUsage.size > 3) score += 0.05;
    if (featureUsage.size > 7) score += 0.1;

    return Math.min(score, 1.0);
  }

  private isPremiumFeature(featureName: string): boolean {
    const premiumFeatures = [
      'advanced_analytics',
      'task_automation',
      'custom_categories',
      'priority_support',
      'bulk_operations',
      'advanced_search',
      'recurring_tasks',
      'team_insights'
    ];
    
    return premiumFeatures.some(feature => featureName.includes(feature));
  }

  /**
   * Get user's current subscription and revenue profile
   */
  getUserRevenueProfile(userId: string): {
    journey_stage: UserJourneyStage;
    feature_usage: Record<string, number>;
    subscription_conversion_probability: number;
    estimated_subscription_value: number;
    estimated_referral_value: number;
    recommended_tier: 'basic' | 'family' | 'premium';
  } {
    const journeyStage = this.userJourneyStage.get(userId) || 'visitor';
    const featureUsage = this.featureUsageCounts.get(userId) || new Map();
    const conversionProbability = this.getConversionProbability(userId);

    // Estimate subscription value (PRIMARY REVENUE)
    let estimatedSubscriptionValue = 0;
    let recommendedTier: 'basic' | 'family' | 'premium' = 'basic';
    
    if (['paid_subscriber', 'premium_subscriber'].includes(journeyStage)) {
      estimatedSubscriptionValue = this.SUBSCRIPTION_REVENUE.family.annual; // Current subscriber
      recommendedTier = 'premium'; // Upsell opportunity
    } else if (journeyStage === 'trial_user') {
      estimatedSubscriptionValue = conversionProbability * this.SUBSCRIPTION_REVENUE.basic.annual;
      recommendedTier = 'basic';
    } else {
      estimatedSubscriptionValue = conversionProbability * this.SUBSCRIPTION_REVENUE.basic.annual;
      
      // Recommend tier based on usage patterns
      const totalUsage = Array.from(featureUsage.values()).reduce((sum, count) => sum + count, 0);
      const familySize = this.estimateFamilySize(featureUsage);
      
      if (familySize > 4 || totalUsage > 50) {
        recommendedTier = 'family';
      } else if (familySize > 8 || totalUsage > 100) {
        recommendedTier = 'premium';
      }
    }

    // Estimate referral value (SECONDARY REVENUE)
    let estimatedReferralValue = 0;
    if (journeyStage === 'referral_user') {
      estimatedReferralValue = 100; // Average referral commission
    } else {
      estimatedReferralValue = conversionProbability * 50; // Expected value (lower priority)
    }

    return {
      journey_stage: journeyStage,
      feature_usage: Object.fromEntries(featureUsage),
      subscription_conversion_probability: conversionProbability,
      estimated_subscription_value: estimatedSubscriptionValue,
      estimated_referral_value: estimatedReferralValue,
      recommended_tier: recommendedTier
    };
  }

  private estimateFamilySize(featureUsage: Map<string, number>): number {
    // Estimate based on family-related feature usage
    const familyFeatures = ['family_invite', 'member_management', 'family_tasks'];
    let familyUsage = 0;
    
    familyFeatures.forEach(feature => {
      featureUsage.forEach((count, featureName) => {
        if (featureName.includes(feature)) {
          familyUsage += count;
        }
      });
    });
    
    // Basic estimation: more family feature usage = larger family
    return Math.min(2 + Math.floor(familyUsage / 5), 10);
  }

  /**
   * Get revenue analytics for dashboard (subscription-focused)
   */
  getRevenueAnalytics(): {
    total_users: number;
    subscription_conversion_rate: number;
    trial_conversion_rate: number;
    average_subscription_value: number;
    average_referral_value: number;
    journey_distribution: Record<UserJourneyStage, number>;
    monthly_recurring_revenue: number;
  } {
    const totalUsers = this.userJourneyStage.size;
    
    // Subscription metrics (primary revenue)
    const subscribedUsers = Array.from(this.userJourneyStage.values())
      .filter(stage => ['paid_subscriber', 'premium_subscriber'].includes(stage)).length;
    const trialUsers = Array.from(this.userJourneyStage.values())
      .filter(stage => stage === 'trial_user').length;
    
    const subscriptionConversionRate = totalUsers > 0 ? subscribedUsers / totalUsers : 0;
    const trialConversionRate = trialUsers > 0 ? subscribedUsers / trialUsers : 0;
    
    // Journey distribution
    const journeyDistribution: Record<UserJourneyStage, number> = {
      visitor: 0,
      registered: 0,
      trial_user: 0,
      paid_subscriber: 0,
      premium_subscriber: 0,
      contractor_interested: 0,
      referral_user: 0,
      power_user: 0,
      inactive: 0
    };

    Array.from(this.userJourneyStage.values()).forEach(stage => {
      journeyDistribution[stage]++;
    });

    // Revenue calculations
    let totalSubscriptionValue = 0;
    let totalReferralValue = 0;
    let monthlyRecurringRevenue = 0;

    Array.from(this.userJourneyStage.keys()).forEach(userId => {
      const profile = this.getUserRevenueProfile(userId);
      totalSubscriptionValue += profile.estimated_subscription_value;
      totalReferralValue += profile.estimated_referral_value;
      
      // Calculate MRR based on journey stage
      const stage = this.userJourneyStage.get(userId);
      if (stage === 'paid_subscriber') {
        monthlyRecurringRevenue += this.SUBSCRIPTION_REVENUE.basic.monthly;
      } else if (stage === 'premium_subscriber') {
        monthlyRecurringRevenue += this.SUBSCRIPTION_REVENUE.premium.monthly;
      }
    });

    const averageSubscriptionValue = totalUsers > 0 ? totalSubscriptionValue / totalUsers : 0;
    const averageReferralValue = totalUsers > 0 ? totalReferralValue / totalUsers : 0;

    return {
      total_users: totalUsers,
      subscription_conversion_rate: subscriptionConversionRate,
      trial_conversion_rate: trialConversionRate,
      average_subscription_value: averageSubscriptionValue,
      average_referral_value: averageReferralValue,
      journey_distribution: journeyDistribution,
      monthly_recurring_revenue: monthlyRecurringRevenue
    };
  }
}

// Export singleton instance
export const revenueTracking = new RevenueTrackingService();

// Helper functions (contractor referral focused)
export const trackRevenue = (event: RevenueEvent) => revenueTracking.trackRevenueEvent(event);
export const trackFeatureUsage = (usage: FeatureUsageEvent) => revenueTracking.trackFeatureUsage(usage);
export const trackOnboardingStep = (userId: string, step: string, completed?: boolean) => revenueTracking.trackOnboardingStep(userId, step, completed);
export const trackABTest = (userId: string, testName: string, variant: string, converted?: boolean) => revenueTracking.trackABTest(userId, testName, variant, converted);
export const getReferralProbability = (userId: string) => revenueTracking.getConversionProbability(userId);
export const getUserRevenueProfile = (userId: string) => revenueTracking.getUserRevenueProfile(userId);

export default revenueTracking;